import {
  type AgentToolResult,
  type AgentToolUpdateCallback,
  type ExtensionContext,
  type Theme,
  type ToolDefinition,
  type ToolRenderResultOptions,
} from "@earendil-works/pi-coding-agent";
import type { Component } from "@earendil-works/pi-tui";
import { logger } from "@feature-forge/core";
import { ForgeConfig } from "@feature-forge/core";
import type { AgentSupervisor } from "@feature-forge/core/agents";
import type { RoutineDefinition } from "@feature-forge/core/flows";
import type { RoutineProgressEvent } from "@feature-forge/core/routines";
import type { RoutineResult } from "@feature-forge/core/routines";
import { RoutineExecutor } from "@feature-forge/core/routines";
import type { TObject, TProperties } from "typebox";
import { Type } from "typebox";

import type { AccumulatedState } from "../tui/progress/AccumulatedState";
import { createAccumulatedState } from "../tui/progress/AccumulatedState";
import { applyEvent } from "../tui/progress/DisplayProjection";
import { NoOpProgressReporter } from "../tui/progress/NoOpProgressReporter";
import { ProgressRenderer } from "../tui/progress/ProgressRenderer";
import type { ProgressWidget } from "../tui/progress/ProgressWidget";
import type { RoutineProgressState } from "../tui/progress/RoutineProgressState";
import { TuiRoutineWidget } from "../tui/progress/TuiRoutineWidget";
import type { AgentViewerHandle } from "../tui/showAgentViewer";
import { showAgentViewer } from "../tui/showAgentViewer";

/**
 * Channels the handler subscribes to for contribution accumulation and
 * progress widget rendering. Agent channels are included so the widget
 * shows agent lifecycle status — the overlay is driven separately via
 * {@link showAgentViewer}.
 */
const PROGRESS_CHANNELS = [
  "feature-forge:workspace-ready",
  "feature-forge:agent-started",
  "feature-forge:agent-stream",
  "feature-forge:agent-done",
  "feature-forge:loop-round-start",
  "feature-forge:loop-round-complete",
  "feature-forge:parallel-start",
  "feature-forge:parallel-done",
  "feature-forge:cleanup-start",
  "feature-forge:cleanup-done",
  "feature-forge:git-start",
  "feature-forge:git-done",
  "feature-forge:shell-start",
  "feature-forge:shell-done",
  "feature-forge:session-set",
  "feature-forge:routine-ref-start",
  "feature-forge:routine-ref-done",
  "feature-forge:routine-ref-error",
] as const;

/**
 * Internal state for tool-row invalidation.
 *
 * The TUI framework stores its `invalidate` callback here so that
 * progress-state changes can trigger tool-row re-renders.
 */
interface ToolRowInvalidation {
  invalidate: (() => void) | undefined;
}

/**
 * Minimal shape of the tool-row render context passed by the pi SDK.
 *
 * `ToolRenderContext` is not publicly exported, so the full context is
 * narrowed to the fields the renderers consume (state + invalidate).
 */
interface ToolRowRenderContext {
  state: ToolRowInvalidation;
  invalidate: () => void;
}

/**
 * Tool adapter that wraps a single routine as a pi tool so the
 * orchestrator LLM can invoke it by name.
 *
 * Each routine gets its own {@link RoutineTool} instance, registered
 * at flow-load time.
 *
 * The tool's parameter schema is built dynamically from the routine's
 * declared `params` array so the LLM receives accurate parameter hints
 * with names and descriptions.
 *
 * Implements {@link RoutineProgressState} so the {@link ProgressRenderer}
 * can read live state without coupling to the tool's internal structure.
 */
export class RoutineTool
  implements
    ToolDefinition<TObject<TProperties>, RoutineResult, ToolRowInvalidation>,
    RoutineProgressState
{
  readonly name: string;
  readonly label: string;
  readonly description: string;
  readonly parameters: TObject<TProperties>;

  /** Private backing fields — exposed through {@link RoutineProgressState} getters. */
  private readonly _routineName: string;

  /** Live accumulated display state, folded from the event stream via applyEvent. */
  private _accumulatedState: AccumulatedState = createAccumulatedState();

  /** Tool-row invalidation handle for renderCall/renderResult. */
  private readonly toolRowState: ToolRowInvalidation = { invalidate: undefined };

  /** Rendering delegate — builds TUI components and widget content from live state. */
  private readonly renderer: ProgressRenderer;

  constructor(
    flowName: string,
    private readonly routineDef: RoutineDefinition,
    private readonly executor: RoutineExecutor,
    private readonly supervisor: AgentSupervisor,
  ) {
    this._routineName = routineDef.id;
    this.name = routineDef.id;
    this.label = `Routine: ${flowName}/${routineDef.id}`;
    this.description = this.buildDescription(routineDef.id, routineDef);
    this.parameters = RoutineTool.buildParamsSchema(routineDef);

    this.renderer = new ProgressRenderer(this);
  }

  // ── RoutineProgressState getters ───────────────────────────

  /** Routine name (e.g. "run_build_loop"). */
  get routineName(): string {
    return this._routineName;
  }

  /** Accumulated display state folded from the event stream. */
  get accumulatedState(): AccumulatedState {
    return this._accumulatedState;
  }

  // ── ToolDefinition rendering ───────────────────────────────

  renderCall = (
    _args: Record<string, unknown>,
    theme: Theme,
    context: ToolRowRenderContext,
  ): Component => {
    context.state.invalidate = context.invalidate;
    this.toolRowState.invalidate = context.invalidate;
    return this.renderer.buildCallComponent(theme);
  };

  renderResult = (
    result: AgentToolResult<RoutineResult>,
    options: ToolRenderResultOptions,
    theme: Theme,
    _context: ToolRowRenderContext,
  ): Component => {
    return this.renderer.buildResultComponent(result, options, theme);
  };

  // ── Tool execution ─────────────────────────────────────────

  async execute(
    toolCallId: string,
    params: Record<string, string>,
    signal: AbortSignal | undefined,
    onUpdate: AgentToolUpdateCallback<RoutineResult> | undefined,
    ctx: ExtensionContext,
  ): Promise<AgentToolResult<RoutineResult>> {
    logger.info("RoutineTool invoked", {
      routine: this._routineName,
      params: Object.keys(params),
    });

    const prompt = params["prompt"] ?? params["_prompt"] ?? "";
    const routineParams: Record<string, string> = {};
    for (const param of this.routineDef.params) {
      if (param.name in params) {
        routineParams[param.name] = params[param.name];
      } else if (param.default !== undefined) {
        routineParams[param.name] = param.default;
      }
    }

    // Reset accumulated state for this execution.
    this.resetState();

    const widget: ProgressWidget = ctx.ui
      ? new TuiRoutineWidget({
          ctx,
          onStateChange: () => {
            this.toolRowState.invalidate?.();
          },
        })
      : new NoOpProgressReporter();

    // Agent viewer overlay — opened lazily on the first agent progress
    // event, not eagerly, so routines without agent steps never create an
    // overlay. The composer owns the full lifecycle (wire → open via
    // ctx.ui.custom → connect → dispose/dismiss). The call is deliberately
    // not awaited: `ctx.ui.custom` resolves only when the overlay is
    // dismissed, so awaiting would stall the routine until the user closes
    // it. The resolved handle is captured for finally — in headless mocks
    // `custom` resolves without opening an overlay and the composer already
    // released the wiring, so finally's dispose is an idempotent safety
    // net; in the TUI the overlay stays open until the user dismisses it
    // and the composer's own dispose tears everything down. If the routine
    // completes first, the handle may not be assigned yet — that is
    // intentional: teardown stays with the composer (its onDone path /
    // headless self-dispose), never with this tool.
    let viewerHandle: AgentViewerHandle | undefined;
    let viewerOpened = false;
    const openViewer = (): void => {
      if (!ctx.hasUI || viewerOpened) return;
      viewerOpened = true;
      void showAgentViewer({
        ctx,
        config: ForgeConfig.getInstance(),
        toolRegistry: this.executor.toolRegistry,
        eventBus: this.executor.eventBus,
        agentQuery: this.supervisor,
      })
        .then((handle) => {
          viewerHandle = handle;
        })
        .catch((err) => {
          logger.warn("Agent viewer overlay creation failed", { err });
        });
    };

    // Read lazily on the first progress event — the flag is only needed when
    // a debug entry is actually written, so avoid config access otherwise.
    let logPayloads: boolean | undefined;
    const handler = (data: unknown): void => {
      const event = data as RoutineProgressEvent;

      // Open the viewer on the first agent-tagged progress event. Only
      // agent-started/stream/done carry `agentId`; all other phases don't,
      // so routines without agent steps never open the overlay.
      const agentId = (event.details as { agentId?: string }).agentId;
      if (agentId) openViewer();

      logPayloads ??= ForgeConfig.getInstance().getLogPayloads();
      logger.debug(
        "RoutineTool progress",
        logPayloads ? { ...event } : { phase: event.phase, message: event.message },
      );

      // Fold the event into the accumulated display state. Stream-only
      // events (agent-stream chunks) are no-ops in the projection.
      applyEvent(this._accumulatedState, event);

      this.renderProgress(widget, ctx);

      if (onUpdate) {
        const resultDetails = event.details as Partial<RoutineResult>;
        onUpdate({
          content: [
            {
              type: "text",
              text: `[${event.phase}] ${event.message}`,
            },
          ],
          details: {
            routine: resultDetails.routine ?? this._routineName,
            passed: resultDetails.passed ?? false,
            status: resultDetails.status ?? "success",
            rounds: resultDetails.rounds ?? 0,
            workspace: resultDetails.workspace,
            results: {},
            summary: resultDetails.summary ?? "",
            session: this.executor.store.toObject(),
          },
        });
      }
    };

    const unsubscribers = PROGRESS_CHANNELS.map((channel) =>
      this.executor.eventBus.on(channel, handler),
    );

    try {
      const result = await this.executor.run(
        this._routineName,
        routineParams,
        prompt,
        signal,
        this.routineDef,
      );

      return {
        content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
        details: result,
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        logger.info("Routine aborted", { routine: this._routineName });
      }
      throw error;
    } finally {
      widget.clear();
      viewerHandle?.dispose();
      unsubscribers.forEach((u) => u());
    }
  }

  // ── Private helpers ────────────────────────────────────────

  /** Reset accumulated display state before each execution. */
  private resetState(): void {
    this._accumulatedState = createAccumulatedState();
  }

  /** Build and render progress surfaces via the renderer. */
  private renderProgress(widget: ProgressWidget, ctx: ExtensionContext): void {
    const theme = ctx.ui?.theme ?? { fg: (_c: string, t: string) => t };
    this.renderer.renderToWidget(widget, theme);
  }

  private static buildParamsSchema(routineDef: RoutineDefinition): TObject<TProperties> {
    const properties: Record<string, ReturnType<typeof Type.String>> = {};
    for (const param of routineDef.params) {
      const schema = Type.String({
        description: param.description,
      });
      properties[param.name] = param.optional ? Type.Optional(schema) : schema;
    }
    return Type.Object(properties);
  }

  private buildDescription(routineName: string, routineDef: RoutineDefinition): string {
    if (routineDef.params.length === 0) {
      return routineDef.description ?? `Run the "${routineName}" routine.`;
    }
    const paramList = routineDef.params
      .map(
        (p) =>
          `${p.name}${p.description ? ` (${p.description})` : ""}${p.optional ? " [optional]" : ""}`,
      )
      .join(", ");
    const base = routineDef.description ?? `Run the "${routineName}" routine with params`;
    return `${base}: ${paramList}.`;
  }
}
